import React, { useMemo } from "react";
import {
    Page,
    Text,
    View,
    Document,
    StyleSheet,
    Image,
    Font,
} from "@react-pdf/renderer";
import { formatDate } from "@util/string";
import { ServiceSheet } from "@models/ServiceSheet";
import { withConditionalCacheBust } from "@util/cache-bust";

Font.register({
    family: "YesevaOne",
    src: "/assets/fonts/YesevaOne-Regular.ttf",
});

export type PageSize = "A4" | "A5";

export interface ServiceSheetDocumentProps {
    serviceSheet: ServiceSheet;
    qrCodeBase64: string;
    pageSize: PageSize;
}

const FIOH_LOGO = "/assets/images/fioh-logo.png";

const getFontSizeForNameAndDate = (name: string): { nameFontSize: number; dateFontSize: number } => {
    if (name.length > 40) return { nameFontSize: 18, dateFontSize: 12 };
    if (name.length > 30) return { nameFontSize: 22, dateFontSize: 14 };
    return { nameFontSize: 24, dateFontSize: 16 };
};

const ServiceSheetDocument: React.FC<ServiceSheetDocumentProps> = ({
    serviceSheet,
    qrCodeBase64,
    pageSize,
}) => {
    const BASE = {
        imageHeight: pageSize === "A4" ? 374 : 300,
        qrCode: 130,
        largeQrCode: 150,
        charityLogo: 38,
        businessLogo: 36,
        footerLogo: 28,
    };

    const scale = useMemo(() => (pageSize === "A5" ? 0.8 : 1), [pageSize]);
    const clamp = (v: number, min: number) => Math.max(min, Math.round(v));

    const { nameFontSize, dateFontSize } = useMemo(
        () => getFontSizeForNameAndDate(`${serviceSheet.firstName} ${serviceSheet.lastName}`),
        [serviceSheet.firstName, serviceSheet.lastName]
    );

    const scaledNameFontSize = clamp(nameFontSize * scale, 14);
    const scaledDateFontSize = clamp(dateFontSize * scale, 10);

    const computed = useMemo(
        () => ({
            image: { ...styles.image, height: Math.round(BASE.imageHeight * scale) },
            qrCode: { ...styles.qrCode, height: Math.round(BASE.qrCode * scale * (pageSize === "A5" ? 1.2 : 1)) },
            largeQrCode: { ...styles.qrCode, height: Math.round(BASE.largeQrCode * scale) },
            qrText: {
                ...styles.qrText,
                fontSize: clamp(12 * scale, 9),
            },

            businessLogo: { ...styles.businessLogo, height: Math.round(BASE.businessLogo * scale) },
            footerLogo: { ...styles.logo, height: Math.round(BASE.footerLogo * scale) },
            title: { ...styles.title, fontSize: scaledNameFontSize },
            dates: { ...styles.dates, fontSize: scaledDateFontSize },
            charityLogo: {
                ...styles.charityLogo,
                height: Math.round(BASE.charityLogo * scale * (pageSize === "A5" ? 1.3 : 1)),
                marginTop: pageSize === "A5" ? 16 : 14,
                marginBottom: pageSize === "A5" ? 24 : -4,
            },
            footer: {
                ...styles.footer,
                bottom: pageSize === "A5" ? 0 : -28,
            },
            reducedFooterMargin: {
                bottom: pageSize === "A5" ? -18 : -60,
            },
        }),
        [scale, pageSize, nameFontSize, dateFontSize],
    );

    return (
        <Document>
            <Page size={pageSize} style={styles.page}>
                <View style={styles.pageContentContainer}>
                    <View style={styles.pageContent}>
                        <View style={styles.header} fixed>
                            {serviceSheet.businessLogo && (
                                <Image
                                    src={{ uri: withConditionalCacheBust(serviceSheet.businessLogo) }}
                                    style={computed.businessLogo}
                                />
                            )}
                        </View>

                        <View style={styles.titleBlock}>
                            {serviceSheet.image && (
                                <Image
                                    src={{ uri: withConditionalCacheBust(serviceSheet.image) }}
                                    style={computed.image}
                                />
                            )}
                            <Text style={computed.title}>
                                {`${serviceSheet.firstName} ${serviceSheet.lastName}`}
                            </Text>
                            {serviceSheet.birthDate && serviceSheet.deathDate && (
                                <Text style={computed.dates}>
                                    {`${formatDate(serviceSheet.birthDate)} — ${formatDate(serviceSheet.deathDate)}`}
                                </Text>
                            )}
                        </View>
                        <View>
                            {qrCodeBase64 && (
                                <>
                                    <Image
                                        src={qrCodeBase64}
                                        style={serviceSheet.charityLogo ? computed.qrCode : computed.largeQrCode}
                                    />
                                    <Text style={computed.qrText}>
                                        Share your memories and tributes
                                    </Text>
                                </>
                            )}
                        </View>
                        <View>
                            {serviceSheet.charityLogo && (
                                <Image
                                    src={{ uri: withConditionalCacheBust(serviceSheet.charityLogo) }}
                                    style={computed.charityLogo}
                                />
                            )}
                        </View>
                        <View
                            fixed
                            style={{
                                ...computed.footer,
                                ...(!serviceSheet.charityLogo ? computed.reducedFooterMargin : {}),
                            }}
                        >
                            <View style={styles.logoContainer}>
                                <Image src={FIOH_LOGO} style={computed.footerLogo} />
                            </View>
                        </View>
                    </View>
                </View>
            </Page>
        </Document>
    );
};

const styles = StyleSheet.create({
    page: {
        fontSize: 12,
        color: "#000",
        fontFamily: "YesevaOne",
    },
    pageContentContainer: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "93.25%",
        padding: 26,
        border: "1px solid #c8c8c8",
        borderRadius: 8,
        margin: "10mm",
        boxSizing: "border-box",
    },
    pageContent: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
    },
    header: {
        display: "flex",
        alignItems: "flex-start",
        marginBottom: 10,
        marginTop: -10,
        marginHorizontal: "auto",
    },
    businessLogo: {
        height: 36,
        width: "auto",
        marginVertical: 4,
    },
    titleBlock: {
        textAlign: "center",
    },
    image: {
        borderRadius: 20,
        marginBottom: 20,
        objectFit: "cover",
        marginLeft: "auto",
        marginRight: "auto",
    },
    title: {
        fontWeight: "bold",
    },
    dates: {
        marginTop: 10,
    },
    footer: {
        position: "absolute",
        left: 0,
        right: 0,
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        paddingHorizontal: 30,
        fontSize: 12,
        color: "#777",
        opacity: 0.8,
    },
    logoContainer: {
        position: "absolute",
        left: 0,
        right: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    logo: {
        height: 28,
    },
    qrSection: {
        alignItems: "center",
        textAlign: "center",
    },
    qrText: {
        marginTop: 12,
        fontSize: 14,
        color: "#2a2a2a",
        textAlign: "center",
        letterSpacing: 0.3,
        marginBottom: -2,
        fontFamily: "Times-Italic",
    },
    qrCode: {
        marginLeft: "auto",
        marginRight: "auto",
        marginTop: 12,
    },
    charityLogo: {
        marginLeft: "auto",
        marginRight: "auto",
    },
});

export default ServiceSheetDocument;
